import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useClipboard } from '@/hooks/dom';
import { useMessageModal } from '@/contexts/layout';
import { Temper } from '@/types/frontend-types';
import {
  POPOVER_COPY_ENTRY_SEPARATOR,
  POPOVER_COPY_FEEDBACK_MS,
  POPOVER_COPY_KEY_VALUE_JOINER,
  TABLE_COPY_ERROR_BODY,
  TABLE_COPY_ERROR_TITLE,
  TABLE_IMAGE_COPY_ERROR_BUTTON_TEXT,
} from './Cmarkdowntable.constants';
import type { CmarkdowntablePopoverEntry } from './Cmarkdowntable.types';

interface UseCmarkdowntablePopoverCopyInput {
  entries: CmarkdowntablePopoverEntry[] | null;
}

interface CmarkdowntablePopoverCopyController {
  didCopy: boolean;
  handleCopyEntries: () => void;
  resetCopyFeedback: () => void;
}

export function useCmarkdowntablePopoverCopy(
  input: UseCmarkdowntablePopoverCopyInput,
): CmarkdowntablePopoverCopyController {
  const { entries } = input;
  const clipboard = useClipboard();
  const { showMessageModal } = useMessageModal();
  const [didCopy, setDidCopy] = useState(false);
  const copyResetRef = useRef<number | null>(null);

  const clearCopyTimer = useCallback((): void => {
    if (copyResetRef.current != null) {
      window.clearTimeout(copyResetRef.current);
      copyResetRef.current = null;
    }
  }, []);

  const resetCopyFeedback = useCallback((): void => {
    clearCopyTimer();
    setDidCopy(false);
  }, [clearCopyTimer]);

  const handleCopyEntries = useCallback((): void => {
    if (!entries) {
      return;
    }

    const text = entries
      .map((entry) => `${entry.title}${POPOVER_COPY_KEY_VALUE_JOINER}${entry.value}`)
      .join(POPOVER_COPY_ENTRY_SEPARATOR);
    // The check mark waits for the write. It used to be set unconditionally,
    // which showed a successful copy over a clipboard the browser had refused.
    clipboard.invoke(
      { text },
      {
        onSuccess: () => {
          setDidCopy(true);
          clearCopyTimer();
          copyResetRef.current = window.setTimeout(() => {
            setDidCopy(false);
            copyResetRef.current = null;
          }, POPOVER_COPY_FEEDBACK_MS);
        },
        onError: () => {
          showMessageModal({
            temper: Temper.Neutral,
            title: TABLE_COPY_ERROR_TITLE,
            body: TABLE_COPY_ERROR_BODY,
            secondaryButtonText: TABLE_IMAGE_COPY_ERROR_BUTTON_TEXT,
          });
        },
      },
    );
  }, [entries, clipboard, clearCopyTimer, showMessageModal]);

  useEffect(() => clearCopyTimer, [clearCopyTimer]);

  return {
    didCopy,
    handleCopyEntries,
    resetCopyFeedback,
  };
}
